'use client'

import { useCallback, useEffect, useRef } from 'react'
import { useBrandContext } from '@/features/brands/brand-context-definition'

/**
 * Guard against imperative post-mutation side effects racing against a brand
 * switch. Capture the current brand slug at click time, then check after the
 * awaited work whether the user is still on the same brand.
 *
 * Cancels most stale-callback bug classes at their callsite in ~3 lines:
 *
 *   const { capture, isStillCurrent } = useCurrentBrandGuard()
 *
 *   const handleClick = async () => {
 *       const captured = capture()
 *       const result = await mutateAsync(...)
 *       if (!isStillCurrent(captured)) return
 *       router.push(...)
 *   }
 *
 * Both callbacks read a live ref (not a render-time closure) so a handler
 * that started before a brand switch still compares against the CURRENT
 * brand after the await — a plain `captured === brandSlug` closure would
 * compare old against old and wave the stale action through.
 *
 * `queryClient.cancelQueries` in BrandContext already aborts every brand-scoped
 * fetch when the URL changes, so most queries reject with AbortError and
 * never reach `.then`. Use this guard for mutations (cancelQueries does not
 * abort mutations) and for post-fetch imperative work like navigation.
 */
export function useCurrentBrandGuard() {
    const { brandSlug } = useBrandContext()

    const liveBrandSlugRef = useRef(brandSlug)
    useEffect(() => {
        liveBrandSlugRef.current = brandSlug
    }, [brandSlug])

    const capture = useCallback(() => liveBrandSlugRef.current, [])

    const isStillCurrent = useCallback(
        (capturedBrandSlug: string) => capturedBrandSlug === liveBrandSlugRef.current,
        []
    )

    return { capture, isStillCurrent }
}
